import React from 'react';
import { AppResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

/**
 * AuthShell - Casca de duas colunas das telas de autenticação (público).
 *
 * Anatomia (mockup auth-*.html, 2026-07-18):
 *  - Painel de MARCA fixo à esquerda (logo topo / headline + features / rodapé),
 *    fundo primário sólido, NÃO rola.
 *  - Coluna de FORMULÁRIO à direita, que rola (overflow-y-auto).
 *  - Mobile (< lg): 1 coluna, a marca some e vira logo compacto no topo do form.
 *
 * O tema é controlado pelo AuthLayout (ThemeProvider/forceMode) — aqui só usamos
 * tokens var(--theme-*); NÃO forçamos claro/escuro nem usamos variante dark:.
 */
const BRAND_FEATURES: { icon: IconName; label: string }[] = [
  { icon: 'message-circle', label: 'Multicanal em uma caixa de entrada' },
  { icon: 'zap', label: 'Agentes de IA que atendem por você' },
  { icon: 'trending-up', label: 'CRM e funis com automações' },
];

interface AuthShellProps {
  app: AppResponse | null;
  children: React.ReactNode;
}

export const AuthShell: React.FC<AuthShellProps> = ({ app, children }) => {
  const appName = app?.name || 'TROIA';
  const initial = appName.charAt(0).toUpperCase();
  const year = new Date().getFullYear();

  return (
    <div
      className="grid min-h-screen grid-cols-1 min-[860px]:h-screen min-[860px]:grid-cols-2 min-[860px]:overflow-hidden"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="auth-shell"
    >
      {/* Painel de marca — fixo, não rola (desktop) */}
      <aside
        className="relative hidden flex-col justify-between overflow-hidden px-[52px] py-12 min-[860px]:flex"
        style={{ backgroundColor: 'var(--theme-button-primary)', color: '#ffffff' }}
      >
        {/* círculos decorativos */}
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-full"
          style={{ right: -80, bottom: -80, width: 320, height: 320, backgroundColor: 'rgba(255,255,255,0.06)' }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-full"
          style={{ left: -60, top: 60, width: 180, height: 180, backgroundColor: 'rgba(255,255,255,0.05)' }}
        />

        {/* logo topo */}
        <div className="relative z-10 flex items-center gap-3">
          {app?.logo ? (
            <img src={app.logo} alt={appName} className="h-10 w-auto" />
          ) : (
            <span
              className="flex h-10 w-10 items-center justify-center rounded-xl text-xl font-black"
              style={{ backgroundColor: '#ffffff', color: 'var(--theme-button-primary)' }}
            >
              {initial}
            </span>
          )}
          <span className="text-xl font-extrabold tracking-tight">{appName}</span>
        </div>

        {/* meio: headline + features */}
        <div className="relative z-10 max-w-[420px]">
          <h2 className="text-3xl font-bold leading-tight tracking-tight">
            Atendimento, CRM e IA em um só lugar.
          </h2>
          <p className="mt-3.5 text-[15px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.85)' }}>
            Centralize WhatsApp, Instagram e e-mail, automatize com agentes de IA e feche mais negócios.
          </p>
          <ul className="mt-6 flex flex-col gap-3.5">
            {BRAND_FEATURES.map((feature) => (
              <li
                key={feature.label}
                className="flex items-center gap-3 text-[15px]"
                style={{ color: 'rgba(255,255,255,0.94)' }}
              >
                <span
                  className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-lg"
                  style={{ backgroundColor: 'rgba(255,255,255,0.16)' }}
                >
                  <Icon name={feature.icon} className="h-4 w-4" />
                </span>
                {feature.label}
              </li>
            ))}
          </ul>
        </div>

        {/* rodapé */}
        <div className="relative z-10 text-[13px]" style={{ color: 'rgba(255,255,255,0.7)' }}>
          © {year} {appName} · Todos os direitos reservados
        </div>
      </aside>

      {/* Coluna de formulário — rola */}
      <main className="flex items-start justify-center overflow-y-auto px-5 pb-8 pt-14 min-[860px]:items-center min-[860px]:px-6 min-[860px]:py-8">
        <div className="w-full max-w-[400px]">
          {/* logo compacto (mobile) */}
          <div className="mb-6 flex items-center gap-2.5 min-[860px]:hidden">
            {app?.logo ? (
              <img src={app.logo} alt={appName} className="h-9 w-auto" />
            ) : (
              <span
                className="flex h-9 w-9 items-center justify-center rounded-lg text-base font-black"
                style={{ backgroundColor: 'var(--theme-button-primary)', color: '#ffffff' }}
              >
                {initial}
              </span>
            )}
            <span className="text-lg font-extrabold tracking-tight" style={{ color: 'var(--theme-text)' }}>
              {appName}
            </span>
          </div>

          {children}
        </div>
      </main>
    </div>
  );
};

AuthShell.displayName = 'AuthShell';
